import React, { useCallback, useEffect, useState } from 'react';
import { parseApiDate } from '../utils/security';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from './TableListFooter';
import TsKayitDavetLinkCard from './TsKayitDavetLinkCard';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';
import {
  createSifreLink,
  fetchSifreLinkleri,
  fetchSifreLinkSecenekleri,
  pasifAlSifreLink,
  updateSifreLinkGecerlilik,
} from '../services/sifreSifirlamaService';

/*
 * Servis Onayları > "Şifremi Unuttum Linkleri"
 * Panel kullanıcılarının SMS ile şifre sıfırlayabileceği süreli link. Link kapalıyken sayfa çalışmaz.
 */

const DURUM = {
  aktif: { label: 'Aktif', bg: '#d1fae5', color: '#065f46' },
  suresi_doldu: { label: 'Süresi doldu', bg: '#f1f5f9', color: '#475569' },
  pasif: { label: 'Kapatıldı', bg: '#fef3c7', color: '#92400e' },
};

const formatTarih = (val) => {
  const d = parseApiDate(val);
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

const hataMesaji = (err, fallback) => {
  const msg = err?.message || err?.data?.mesaj || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

export default function SifreSifirlamaLinkleriPanel() {
  const { confirm } = useConfirmDialog();
  const [secenekler, setSecenekler] = useState([]);
  const [yeniSure, setYeniSure] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [olusturuluyor, setOlusturuluyor] = useState(false);
  const [sonLink, setSonLink] = useState('');
  const [islemId, setIslemId] = useState('');
  const [uzatma, setUzatma] = useState(null); // { id, sureMs }
  const [sayfa, setSayfa] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchSifreLinkSecenekleri();
        const list = res?.veri?.secenekler || [];
        setSecenekler(list);
        const v = res?.veri?.varsayilanSureMs;
        setYeniSure(String(list.find((s) => s.sureMs === v)?.sureMs ?? list[0]?.sureMs ?? ''));
      } catch {
        setSecenekler([]);
      }
    })();
  }, []);

  const yukle = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchSifreLinkleri({ sayfa, limit: PAGE_DEFAULT_LIMIT });
      if (res?.basarili === false) {
        setError(res.mesaj || 'Liste yüklenemedi');
        setItems([]);
        return;
      }
      setItems(res?.veri?.items || []);
      setMeta(normalizeSayfalama(res?.veri?.sayfalama));
    } catch (err) {
      setError(hataMesaji(err, 'Liste yüklenemedi'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [sayfa]);

  useEffect(() => { yukle(); }, [yukle]);

  const linkAc = async () => {
    const ms = parseInt(yeniSure, 10);
    if (!Number.isFinite(ms)) return;
    setOlusturuluyor(true);
    setSonLink('');
    try {
      const res = await createSifreLink(ms);
      if (res?.basarili === false) {
        window.showNotification?.('error', res.mesaj || 'Link oluşturulamadı');
        return;
      }
      setSonLink(res?.veri?.url || '');
      window.showNotification?.('success', 'Şifremi unuttum linki açıldı');
      setSayfa(1);
      yukle();
    } catch (err) {
      window.showNotification?.('error', hataMesaji(err, 'Link oluşturulamadı'));
    } finally {
      setOlusturuluyor(false);
    }
  };

  const kapat = async (item) => {
    const ok = await confirm({
      title: 'Linki kapat',
      message: 'Bu link kapatılacak; linkle başlamış, tamamlanmamış şifre sıfırlamalar da iptal olur.',
      confirmLabel: 'Kapat',
      variant: 'danger',
    });
    if (!ok) return;
    setIslemId(item.id);
    try {
      const res = await pasifAlSifreLink(item.id);
      if (res?.basarili === false) {
        window.showNotification?.('error', res.mesaj || 'Kapatılamadı');
        return;
      }
      window.showNotification?.('success', 'Link kapatıldı');
      yukle();
    } catch (err) {
      window.showNotification?.('error', hataMesaji(err, 'Kapatılamadı'));
    } finally {
      setIslemId('');
    }
  };

  const uzat = async () => {
    if (!uzatma) return;
    setIslemId(uzatma.id);
    try {
      const res = await updateSifreLinkGecerlilik(uzatma.id, parseInt(uzatma.sureMs, 10));
      if (res?.basarili === false) {
        window.showNotification?.('error', res.mesaj || 'Güncellenemedi');
        return;
      }
      window.showNotification?.('success', 'Süre güncellendi');
      setUzatma(null);
      yukle();
    } catch (err) {
      window.showNotification?.('error', hataMesaji(err, 'Güncellenemedi'));
    } finally {
      setIslemId('');
    }
  };

  const kopyala = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      window.showNotification?.('success', 'Link kopyalandı');
    } catch {
      window.showNotification?.('error', 'Kopyalanamadı');
    }
  };

  return (
    <>
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="sifre-link-sure" style={{ fontSize: 13, color: 'var(--text-gray)' }}>
              Link ne kadar açık kalsın?
            </label>
            <select
              id="sifre-link-sure"
              value={yeniSure}
              onChange={(e) => setYeniSure(e.target.value)}
              disabled={olusturuluyor || secenekler.length === 0}
              style={{ minWidth: 160, padding: '8px 10px', borderRadius: 8 }}
            >
              {secenekler.map((s) => (
                <option key={s.sureMs} value={String(s.sureMs)}>{s.label}</option>
              ))}
            </select>
          </div>
          <button type="button" className="primary-btn" onClick={linkAc} disabled={olusturuluyor || !yeniSure}>
            <i className="fas fa-key" />
            <span>{olusturuluyor ? 'Açılıyor...' : 'Şifremi Unuttum Linki Aç'}</span>
          </button>
        </div>
        <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--text-gray)' }}>
          Panel kullanıcıları bu linkten servisini seçer, kayıtlı numarasını doğrular ve SMS ile gelen
          kullanıcı adı + kodla yeni şifre belirler. Süre dolunca veya link kapatılınca sayfa çalışmaz.
        </p>
      </div>

      {sonLink && <TsKayitDavetLinkCard url={sonLink} />}

      <div className="card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : items.length === 0 ? (
          <div className="empty-state" style={{ padding: 40, textAlign: 'center' }}>
            <i className="fas fa-key" style={{ fontSize: 32, color: 'var(--text-light)', marginBottom: 12 }} />
            <div style={{ color: 'var(--text-gray)', fontSize: 14 }}>Henüz şifremi unuttum linki açılmadı</div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Durum</th>
                    <th>Bitiş</th>
                    <th>Açılış</th>
                    <th>SMS / Tamamlanan</th>
                    <th>Link</th>
                    <th className="islemler-sutunu">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const d = DURUM[item.durum] || DURUM.suresi_doldu;
                    return (
                      <tr key={item.id}>
                        <td>
                          <span className="bo-badge" style={{ backgroundColor: d.bg, color: d.color }}>{d.label}</span>
                        </td>
                        <td>{formatTarih(item.gecerlilikBitis)}</td>
                        <td>
                          {formatTarih(item.createdAt)}
                          {item.olusturanKullanici?.kullaniciAdi ? (
                            <small style={{ display: 'block', color: 'var(--text-gray)' }}>
                              {item.olusturanKullanici.kullaniciAdi}
                            </small>
                          ) : null}
                        </td>
                        <td>{item.smsSayisi} / {item.tamamlananSayisi}</td>
                        <td className="bo-url-cell" title={item.url}>
                          <span className={item.durum !== 'aktif' ? 'bo-url-muted' : undefined}>{item.url}</span>
                        </td>
                        <td className="islemler-sutunu">
                          <div className="table-actions">
                            <button type="button" className="category-action-btn edit-btn" title="Kopyala" onClick={() => kopyala(item.url)}>
                              <i className="fas fa-copy" />
                            </button>
                            {item.durum !== 'pasif' && (
                              <button
                                type="button"
                                className="category-action-btn edit-btn"
                                title={item.durum === 'aktif' ? 'Süreyi değiştir' : 'Yeniden aç'}
                                onClick={() => setUzatma({ id: item.id, sureMs: yeniSure })}
                              >
                                <i className="fas fa-clock" />
                              </button>
                            )}
                            {item.durum === 'aktif' && (
                              <button
                                type="button"
                                className="category-action-btn delete-btn"
                                title="Linki kapat"
                                disabled={islemId === item.id}
                                onClick={() => kapat(item)}
                              >
                                <i className="fas fa-ban" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={meta.total}
              currentPage={sayfa}
              totalPages={meta.totalPages}
              onPageChange={setSayfa}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>

      {uzatma && (
        <div className="modal-overlay" onClick={() => setUzatma(null)} role="presentation">
          <div className="modal-content" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2><i className="fas fa-clock" /> Link süresi</h2>
              <button type="button" className="modal-close-btn" onClick={() => setUzatma(null)} aria-label="Kapat">
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <label htmlFor="sifre-link-uzat" style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
                Şu andan itibaren açık kalma süresi
              </label>
              <select
                id="sifre-link-uzat"
                value={uzatma.sureMs}
                onChange={(e) => setUzatma((u) => ({ ...u, sureMs: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8 }}
              >
                {secenekler.map((s) => (
                  <option key={s.sureMs} value={String(s.sureMs)}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="modal-footer">
              <button type="button" className="secondary-btn" onClick={() => setUzatma(null)}>İptal</button>
              <button type="button" className="primary-btn" onClick={uzat} disabled={islemId === uzatma.id}>Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
